/* eslint-disable no-console */
import { DraftTask, Task, ValidationType, WorkModel } from 'models/task.model';
import create from 'zustand';
import {
  DraftSetOptions,
  DraftTaskKeys,
  DraftTaskValidity,
  TaskActions,
  TaskState,
} from './task.type';

const tempTask: Task = {
  id: '',
  title: '',
  description: '',
  workModels: [],
  validator: 'NULL',
  answer: '',
  errorMessage: '',
  status: 'NOT_SAVED',
  author: null,
  createdAt: '',
  updatedAt: '',
};

const draftTask: DraftTaskKeys = {
  title: '',
  description: '',
  workModels: '',
  validator: '',
  answer: '',
  errorMessage: '',
  status: '',
};

const draftTaskValidity: DraftTaskValidity = {
  title: false,
  description: false,
  workModels: false,
  validator: false,
  answer: true,
  errorMessage: true,
};

const initialState: TaskState = {
  tasks: [],
  draft: tempTask,
  active: tempTask,
  isFormValid: false,
  fieldValidity: draftTaskValidity,
  errors: draftTask,
};

const updateTaskList = (
  state: TaskState,
  options: DraftSetOptions & {
    field: keyof DraftTask;
    value: string | WorkModel[] | ValidationType;
  },
) => {
  const { draft, tasks } = state;
  const { field, value, markAsDirty } = options;
  const draftTaskIndex = tasks.findIndex((task) => task.id === draft.id);

  if (draftTaskIndex === -1) {
    return { updatedTasks: tasks };
  }

  const updatedTasks: Task[] = [
    ...tasks.slice(0, draftTaskIndex),
    {
      ...draft,
      [field]: value,
      ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
    },
    ...tasks.slice(draftTaskIndex + 1),
  ];

  return { updatedTasks };
};

const updateTaskField = (
  state: TaskState,
  options: DraftSetOptions & {
    field: keyof DraftTask;
    value: string | WorkModel[] | ValidationType;
  },
): Partial<TaskState> => {
  const { draft, fieldValidity, errors } = state;
  const { field, value, markAsDirty } = options;
  const { updatedTasks } = updateTaskList(state, {
    field,
    value,
    markAsDirty,
  });

  return {
    isFormValid: Object.values(fieldValidity).every((bool) => bool),
    fieldValidity: {
      ...fieldValidity,
      [field]: true,
    },
    errors: {
      ...errors,
      [field]: '',
    },
    draft: {
      ...draft,
      [field]: value,
      ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
    },
    tasks: updatedTasks,
  };
};

export const useTaskStore = create<TaskState & TaskActions>((set) => ({
  ...initialState,
  actions: {
    setDraftTitle: (
      title: string,
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) => {
      const characterLimit = 100;

      set((state) => {
        const { draft, fieldValidity, errors } = state;
        const { updatedTasks } = updateTaskList(state, {
          field: 'title',
          value: title ?? 'Title',
          markAsDirty,
        });

        if (title.length > characterLimit) {
          return {
            isFormValid: false,
            draft: {
              ...draft,
              title,
              ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
            },
            fieldValidity: {
              ...fieldValidity,
              title: false,
            },
            errors: {
              ...errors,
              title: `Title must be less than 100 characters long. You are ${
                title.length - characterLimit
              } characters over`,
            },
            tasks: updatedTasks,
          };
        }

        if (title.length === 0) {
          return {
            ...state,
            fieldValidity: {
              ...state.fieldValidity,
              title: false,
            },
            draft: {
              ...state.draft,
              title,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          markAsDirty,
          field: 'title',
          value: title,
        });
      });
    },
    setDraftDescription: (
      description: string,
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) => {
      const characterLimit = 900;

      set((state) => {
        const { draft, fieldValidity, errors } = state;
        const { updatedTasks } = updateTaskList(state, {
          field: 'description',
          value: description,
          markAsDirty,
        });

        if (description.length > characterLimit) {
          return {
            isFormValid: false,
            draft: {
              ...draft,
              description,
              ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
            },
            fieldValidity: {
              ...fieldValidity,
              description: false,
            },
            errors: {
              ...errors,
              description: `Description must be less than 900 characters long. You are ${
                description.length - characterLimit
              } characters over`,
            },
            tasks: updatedTasks,
          };
        }

        if (description.length === 0) {
          return {
            ...state,
            fieldValidity: {
              ...state.fieldValidity,
              description: false,
            },
            errors: {
              ...state.errors,
              description: '',
            },
            draft: {
              ...state.draft,
              description,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          field: 'description',
          value: description,
          markAsDirty,
        });
      });
    },
    setDraftWorkModels: (
      workModels: WorkModel[],
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) => {
      set((state) => {
        if (workModels.length === 0) {
          return {
            ...state,
            fieldValidity: {
              ...state.fieldValidity,
              workModels: false,
            },
            draft: {
              ...state.draft,
              workModels,
            },
          };
        }

        return updateTaskField(state, {
          field: 'workModels',
          value: workModels,
          markAsDirty,
        });
      });
    },
    setDraftValidator: (
      validator: ValidationType,
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) => {
      set((state) => {
        const { draft, fieldValidity, errors } = state;
        const { updatedTasks } = updateTaskList(state, {
          field: 'validator',
          value: validator,
          markAsDirty,
        });

        if (validator === 'CONTAINS' && draft.answer.length === 0) {
          return {
            isFormValid: Object.values(fieldValidity).every((bool) => bool),
            fieldValidity: {
              ...fieldValidity,
              answer: false,
            },
            errors: {
              ...errors,
              answer: 'Answer is required for this validation type',
            },
            draft: {
              ...draft,
              validator,
              ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
            },
            tasks: updatedTasks,
          };
        }

        if (validator === 'NULL') {
          return {
            ...state,
            fieldValidity: {
              ...state.fieldValidity,
              validator: false,
            },
            draft: {
              ...state.draft,
              validator,
            },
          };
        }

        return {
          isFormValid: Object.values(fieldValidity).every((bool) => bool),
          fieldValidity: {
            ...fieldValidity,
            validator: true,
            answer: true,
          },
          errors: {
            ...errors,
            answer: '',
          },
          draft: {
            ...draft,
            validator,
            ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
            answer: '',
          },
          tasks: updatedTasks,
        };
      });
    },
    setDraftAnswer: (
      answer: string,
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) => {
      const characterLimit = 100;

      set((state) => {
        const { draft, fieldValidity, errors } = state;
        const { updatedTasks } = updateTaskList(state, {
          field: 'answer',
          value: answer,
          markAsDirty,
        });

        if (state.draft.validator !== 'CONTAINS') {
          return state;
        }

        if (answer.length === 0) {
          return {
            isFormValid: false,
            draft: {
              ...draft,
              answer,
              ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
            },
            fieldValidity: {
              ...fieldValidity,
              answer: false,
            },
            errors: {
              ...errors,
              answer: 'Answer is required for this validation type',
            },
            tasks: updatedTasks,
          };
        }

        if (answer.length > characterLimit) {
          return {
            isFormValid: false,
            draft: {
              ...draft,
              answer,
              ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
            },
            fieldValidity: {
              ...fieldValidity,
              answer: false,
            },
            errors: {
              ...errors,
              answer: `Answer must be less than 100 characters long. You are ${
                answer.length - characterLimit
              } characters over`,
            },
            tasks: updatedTasks,
          };
        }

        return {
          isFormValid: Object.values(fieldValidity).every((bool) => bool),
          fieldValidity: {
            ...fieldValidity,
            validator: true,
            answer: true,
          },
          errors: {
            ...errors,
            answer: '',
          },
          draft: {
            ...draft,
            answer,
            ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
          },
          tasks: updatedTasks,
        };
      });
    },
    setDraftErrorMessage: (
      errorMessage: string,
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) => {
      const characterLimit = 300;

      set((state) => {
        const { draft, fieldValidity, errors } = state;
        const { updatedTasks } = updateTaskList(state, {
          field: 'errorMessage',
          value: errorMessage,
          markAsDirty,
        });

        if (errorMessage.length > characterLimit) {
          return {
            isFormValid: false,
            draft: {
              ...draft,
              errorMessage,
              ...(markAsDirty ? { status: 'NOT_SAVED' } : {}),
            },
            fieldValidity: {
              ...fieldValidity,
              errorMessage: false,
            },
            errors: {
              ...errors,
              errorMessage: `Answer must be less than 300 characters long. You are ${
                errorMessage.length - characterLimit
              } characters over`,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          field: 'errorMessage',
          value: errorMessage,
          markAsDirty,
        });
      });
    },
  },
}));

export const setTasks = (tasks: Task[]) => {
  useTaskStore.setState({ tasks });
};

export const resetDraft = () => {
  useTaskStore.setState({
    draft: initialState.draft,
    errors: initialState.errors,
    fieldValidity: initialState.fieldValidity,
    isFormValid: initialState.isFormValid,
  });
};

export const setDraftTask = (task: Task) => {
  const {
    setDraftTitle,
    setDraftDescription,
    setDraftWorkModels,
    setDraftValidator,
    setDraftAnswer,
    setDraftErrorMessage,
  } = useTaskStore.getState().actions;

  useTaskStore.setState({ draft: task });

  if (task) {
    setDraftTitle(task?.title, { markAsDirty: false });
    setDraftDescription(task?.description, { markAsDirty: false });
    setDraftWorkModels(task?.workModels, { markAsDirty: false });
    setDraftValidator(task?.validator, { markAsDirty: false });
    setDraftAnswer(task?.answer, { markAsDirty: false });
    setDraftErrorMessage(task?.errorMessage, { markAsDirty: false });
  }
};

export const deleteDraft = (id: string) => {
  useTaskStore.setState((state) => ({
    tasks: state.tasks.filter((task) => task.id !== id),
  }));
};
